import { useEffect, useRef, useState } from "react";
import { Upload, X, Check, Camera, Receipt, Sparkles, FileText, Images } from "lucide-react";
import { toast } from "sonner";
import driverApi from "@/lib/driverApi";
import { compressImageFile } from "@/lib/imageCompression";

export const EXPENSE_CATEGORIES = [
  { slug: "gorivo",   label: "Gorivo",              emoji: "⛽" },
  { slug: "dijelovi", label: "Dijelovi za vozilo",  emoji: "🔩" },
  { slug: "servis",   label: "Servis vozila",       emoji: "🛠️" },
  { slug: "gume",     label: "Gume",                emoji: "🛞" },
  { slug: "parking",  label: "Parking i cestarine", emoji: "🅿️" },
  { slug: "pranje",   label: "Pranje i održavanje", emoji: "🧼" },
];

/**
 * iter75.3 — Driver App fuel-receipt upload with automatic OCR.
 *
 * Flow: snap → `/driver/fuel-receipts/scan` (Gemini Vision OCR) →
 * auto-fill fields → driver confirms → `/driver/fuel-receipts/upload`.
 */
const PENDING_KEY = "juke_receipt_pending";

export default function DriverReceiptUpload({ onClose, onDone }) {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const compressedRef = useRef(null);
  const previewUrlRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [amount, setAmount] = useState("");
  const [receiptDate, setReceiptDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [note, setNote] = useState("");
  const [category, setCategory] = useState("gorivo");
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [ocr, setOcr] = useState(null);

  // --- Android camera eviction recovery -----------------------------------
  // Opening the native camera can make Android kill the tab to free memory;
  // the page then reloads and all state is lost ("app crashed to home").
  // We persist the form before launching the camera and restore it on mount.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(PENDING_KEY);
      if (raw) {
        const p = JSON.parse(raw);
        if (p.category) setCategory(p.category);
        if (p.amount) setAmount(p.amount);
        if (p.receiptDate) setReceiptDate(p.receiptDate);
        if (p.note) setNote(p.note);
        if (p.cameraLaunched) {
          toast.info("Nastavljamo unos računa — ponovno odaberite fotografiju.");
        }
      }
    } catch { /* corrupt entry — ignore */ }
    return () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const savePending = (extra = {}) => {
    try {
      sessionStorage.setItem(PENDING_KEY, JSON.stringify({
        category, amount, receiptDate, note, ts: Date.now(), ...extra,
      }));
    } catch { /* storage full — non-fatal */ }
  };
  const clearPending = () => { try { sessionStorage.removeItem(PENDING_KEY); } catch { /* noop */ } };
  const close = () => { clearPending(); onClose?.(); };

  const openPicker = (ref, camera) => {
    try {
      savePending({ cameraLaunched: camera });
      if (ref.current) {
        ref.current.value = ""; // same-photo retake + releases the old File
        ref.current.click();
      }
    } catch (err) {
      toast.error("Kamera se ne može otvoriti — pokušajte 'Iz galerije / PDF'.");
    }
  };

  const runScan = async (file) => {
    setScanning(true);
    setOcr(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await driverApi.post("/driver/fuel-receipts/scan", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fields = data?.ocr || {};
      setOcr(fields);
      if (fields.total_amount_eur != null) setAmount(String(fields.total_amount_eur));
      if (fields.date_iso) {
        // Strict year lock — always the current year, never in the future.
        const todayIso = new Date().toISOString().slice(0, 10);
        let d = `${todayIso.slice(0, 4)}${fields.date_iso.slice(4)}`;
        if (d > todayIso) d = todayIso;
        setReceiptDate(d);
      }
      if (fields.station_name || fields.fuel_type) {
        // Contextual note — fuel type is only relevant for the fuel category.
        const noteBits = category === "gorivo"
          ? [fields.station_name, fields.fuel_type].filter(Boolean)
          : [fields.station_name].filter(Boolean);
        if (noteBits.length) setNote(noteBits.join(" · "));
      }
      toast.success(
        fields.total_amount_eur
          ? `Skenirano — €${Number(fields.total_amount_eur).toFixed(2)}`
          : "Skenirano — provjerite iznos"
      );
    } catch (err) {
      toast.error("Automatsko skeniranje nije uspjelo — iznos možete upisati ručno.");
    } finally {
      setScanning(false);
    }
  };

  const handleFile = async (f) => {
    if (!f) return;
    try {
      setFileName(f.name || "");
      const isPdf = (f.type || "").includes("pdf") || /\.pdf$/i.test(f.name || "");
      // Compress FIRST, preview from the small file — previewing the raw
      // 12 MP photo decodes it full-size and spikes memory on phones.
      let upload = f;
      if (!isPdf) {
        try {
          upload = await compressImageFile(f, {
            maxDim: 2600, targetBytes: 4 * 1024 * 1024, minQuality: 0.65,
          });
        } catch { upload = f; }
      }
      compressedRef.current = upload;
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = isPdf ? null : URL.createObjectURL(upload);
      setPreview(isPdf ? "pdf" : previewUrlRef.current);
      await runScan(upload);
    } catch (err) {
      compressedRef.current = compressedRef.current || f;
      toast.error("Obrada fotografije nije uspjela — pokušajte ponovno ili odaberite iz galerije.");
    }
  };

  const onCameraChange = () => handleFile(cameraRef.current?.files?.[0]);
  const onGalleryChange = () => handleFile(galleryRef.current?.files?.[0]);

  const submit = async (e) => {
    e.preventDefault();
    const f = compressedRef.current;
    if (!f) return toast.error("Najprije fotografirajte ili odaberite račun");
    const amtVal = Number(amount);
    if (!(amtVal > 0)) return toast.error("Unesite ukupni iznos");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("amount_eur", String(amtVal));
      fd.append("category", category);
      fd.append("receipt_date", receiptDate);
      if (note) fd.append("note", note);
      if (ocr) {
        for (const [k, v] of Object.entries(ocr)) {
          if (v === null || v === undefined) continue;
          if (["raw_text", "date_iso", "total_amount_eur"].includes(k)) continue;
          fd.append(k, String(v));
        }
      }
      const { data } = await driverApi.post("/driver/fuel-receipts/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      clearPending();
      toast.success(
        data.refund_eur > 0
          ? `Poslano. Povrat čeka odobrenje: €${Number(data.refund_eur).toFixed(2)}`
          : "Poslano — čeka pregled administratora."
      );
      if (onDone) onDone(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Slanje nije uspjelo");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={close}
      data-testid="driver-receipt-modal"
    >
      <div
        className="w-full max-w-md bg-[#0a0a0a] border border-white/10 sm:rounded-2xl rounded-t-2xl p-5 max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-[#C5A059]" />
            <h3 className="text-lg font-light">Unos računa / troška</h3>
          </div>
          <button onClick={close} className="text-zinc-400 hover:text-white p-1" data-testid="receipt-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!result ? (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Kategorija troška</div>
              <div className="grid grid-cols-3 gap-1.5" data-testid="receipt-category-picker">
                {EXPENSE_CATEGORIES.map((c) => (
                  <button
                    type="button"
                    key={c.slug}
                    onClick={() => setCategory(c.slug)}
                    data-testid={`receipt-category-${c.slug}`}
                    className={`px-1.5 py-2 rounded-xl border text-center transition-colors ${
                      category === c.slug
                        ? "border-[#C5A059] bg-[#C5A059]/10 text-white"
                        : "border-white/10 text-zinc-400 hover:border-white/30"
                    }`}
                  >
                    <div className="text-base leading-none">{c.emoji}</div>
                    <div className="text-[9px] mt-1 leading-tight">{c.label}</div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Račun (fotografija ili PDF)</div>
              <div className="relative border border-dashed border-white/15 rounded-xl overflow-hidden">
                {preview === "pdf" ? (
                  <div className="py-8 flex flex-col items-center gap-2 text-zinc-300" data-testid="receipt-pdf-preview">
                    <FileText className="w-10 h-10 text-[#C5A059]" />
                    <div className="text-sm max-w-[260px] truncate">{fileName || "PDF račun"}</div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">PDF — 1. stranica se skenira</div>
                  </div>
                ) : preview ? (
                  <img src={preview} alt="Receipt" className="w-full max-h-56 object-contain bg-black" />
                ) : (
                  <div className="py-6 flex flex-col items-center gap-2 text-zinc-500">
                    <Receipt className="w-7 h-7" />
                    <div className="text-[10px] uppercase tracking-widest text-[#C5A059]">Automatsko skeniranje uključeno</div>
                  </div>
                )}
                {scanning && (
                  <div className="absolute inset-0 bg-black/70 flex items-center justify-center gap-2 text-[#C5A059] text-sm">
                    <Sparkles className="w-4 h-4 animate-pulse" /> Skeniranje računa…
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => openPicker(cameraRef, true)}
                  data-testid="receipt-camera-btn"
                  className="h-11 rounded-xl border border-[#C5A059]/50 text-[#C5A059] text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-[#C5A059]/10 transition-colors"
                >
                  <Camera className="w-4 h-4" /> Fotografiraj
                </button>
                <button
                  type="button"
                  onClick={() => openPicker(galleryRef, false)}
                  data-testid="receipt-gallery-btn"
                  className="h-11 rounded-xl border border-white/15 text-zinc-300 text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:border-[#C5A059]/50 transition-colors"
                >
                  <Images className="w-4 h-4" /> Iz galerije / PDF
                </button>
              </div>
              <input
                ref={cameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={onCameraChange}
                className="hidden"
                data-testid="receipt-camera-input"
              />
              <input
                ref={galleryRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                onChange={onGalleryChange}
                className="hidden"
                data-testid="receipt-file-input"
              />
            </div>

            {ocr && (
              <div className="rounded-xl border border-[#C5A059]/30 bg-[#C5A059]/5 p-3 text-xs">
                <div className="text-[10px] uppercase tracking-widest text-[#C5A059] mb-1 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Očitano s računa
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-zinc-300">
                  {ocr.station_name && <Row k="Postaja" v={ocr.station_name} />}
                  {ocr.fuel_type && <Row k="Gorivo" v={ocr.fuel_type} />}
                  {ocr.invoice_number && <Row k="Br. računa" v={ocr.invoice_number} />}
                  {ocr.time && <Row k="Vrijeme" v={ocr.time} />}
                  {ocr.vat_amount_eur != null && <Row k="PDV" v={`€${Number(ocr.vat_amount_eur).toFixed(2)}`} />}
                  {ocr.payment_method && <Row k="Plaćeno" v={ocr.payment_method === "cash" ? "gotovina" : ocr.payment_method === "card" ? "kartica" : ocr.payment_method} />}
                  {ocr.buyer_oib && <Row k="OIB kupca" v={ocr.buyer_oib} />}
                  {ocr.issuer_oib && <Row k="OIB izdavatelja" v={ocr.issuer_oib} />}
                  {ocr.jir && <Row k="JIR" v={ocr.jir} short />}
                  {ocr.zki && <Row k="ZKI" v={ocr.zki} short />}
                </div>
              </div>
            )}

            <label className="block">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Iznos (EUR)</div>
              <input
                type="number" step="0.01" min="0" inputMode="decimal"
                value={amount} onChange={(e) => setAmount(e.target.value)}
                placeholder="30.00"
                className="w-full bg-black/60 border border-white/10 px-3 py-3 rounded-lg text-base focus:outline-none focus:border-[#C5A059]/60"
                data-testid="receipt-amount"
              />
            </label>

            <label className="block">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Datum</div>
              <input
                type="date" value={receiptDate}
                min={`${new Date().getFullYear()}-01-01`}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setReceiptDate(e.target.value)}
                className="w-full bg-black/60 border border-white/10 px-3 py-3 rounded-lg text-sm focus:outline-none focus:border-[#C5A059]/60"
                data-testid="receipt-date"
              />
            </label>

            <label className="block">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Napomena (neobavezno)</div>
              <input
                value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="INA · Eurodizel"
                className="w-full bg-black/60 border border-white/10 px-3 py-3 rounded-lg text-sm focus:outline-none focus:border-[#C5A059]/60"
                data-testid="receipt-note"
              />
            </label>

            <button
              type="submit" disabled={busy || scanning}
              className="w-full bg-[#C5A059] text-black font-semibold rounded-full py-3 text-sm uppercase tracking-widest disabled:opacity-40 inline-flex items-center justify-center gap-2"
              data-testid="receipt-submit"
            >
              {busy
                ? <><Upload className="w-4 h-4 animate-pulse" /> Slanje…</>
                : <><Upload className="w-4 h-4" /> Pošalji račun</>}
            </button>
          </form>
        ) : (
          <div className="py-6 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mb-4">
              <Check className="w-7 h-7 text-emerald-400" />
            </div>
            <h4 className="text-lg font-light mb-1">Račun poslan</h4>
            <p className="text-sm text-zinc-400 mb-1">
              Iznos <span className="text-white">€{Number(result.amount_eur).toFixed(2)}</span>
            </p>
            <p className="text-sm text-[#C5A059] mb-6">
              Procijenjeni povrat: €{Number(result.refund_eur || 0).toFixed(2)}
            </p>
            <button onClick={close} className="px-6 py-2 rounded-full border border-white/10 text-sm" data-testid="receipt-done">
              Gotovo
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ k, v, short }) {
  return (
    <>
      <div className="text-zinc-500">{k}</div>
      <div className={`truncate ${short ? "font-mono text-[10px]" : ""}`}>{v}</div>
    </>
  );
}
